import { FC } from 'react';

import { cn } from '@/lib/utils';

import { ImageState } from './ImageCompareDialog';
import ImageControls from './ImageControls';

interface ImageDisplayProps {
  image: ImageState;
  onTransform: (transform: { scale: number; rotation: number }) => void;
  isFirst?: boolean;
}

const ImageDisplay: FC<ImageDisplayProps> = ({
  image,
  onTransform,
  isFirst,
}) => {
  const openInNewTab = () => {
    const win = window.open('/viewer.html', '_blank');
    if (!win) return;

    const onReady = (e: MessageEvent) => {
      if (e.data?.type === 'viewer_ready') {
        const { onTransform: _omit, ...serializableImage } = image as any;
        win.postMessage(
          {
            type: 'init_viewer',
            payload: {
              items: [serializableImage],
              currentIndex: 0,
            },
          },
          '*',
        );
        window.removeEventListener('message', onReady);
      }
    };

    window.addEventListener('message', onReady);
  };
  return (
    <div
      className={cn(
        'sm:min-h-auto relative flex-1 overflow-hidden rounded-md border border-dashed border-separator sm:h-[600px]',
        isFirst ? 'order-1 sm:order-none' : 'order-3 sm:order-none',
      )}
    >
      <img
        src={image.webp_url || image.file_link || ''}
        alt={image.description || image.name || 'image'}
        loading="lazy"
        decoding="async"
        className="h-auto w-full object-contain transition-transform duration-200 sm:h-full"
        style={{
          transform: `scale(${image.transform.scale}) rotate(${image.transform.rotation}deg)`,
        }}
        onError={(e) => {
          // Fallback to original file_link if webp fails
          const target = e.currentTarget as HTMLImageElement;
          if (image.webp_url && target.src !== (image.file_link || '')) {
            target.src = image.file_link || '';
          }
        }}
      />
      <ImageControls
        onRotate={(direction) => {
          const rawRotation =
            direction === 'up'
              ? image.transform.rotation + 90
              : image.transform.rotation - 90;
          const normalized = ((rawRotation % 360) + 360) % 360;
          onTransform({ ...image.transform, rotation: normalized });
        }}
        onZoom={(direction) => {
          const newScale =
            direction === 'in'
              ? Math.min(image.transform.scale + 0.25, 3)
              : Math.max(image.transform.scale - 0.25, 0.5);
          onTransform({ ...image.transform, scale: newScale });
        }}
        imageOnNewPage={openInNewTab}
      />
    </div>
  );
};

export default ImageDisplay;
